/**
 * Capability descriptor — discriminated union of all 13 capabilities, each
 * carrying optional view-aspect and/or edit-aspect markers.
 *
 * P-X stage: aspect interfaces are pure marker interfaces with a `_kind`
 * literal. Subscriber classes (renderer / editor-controller wiring) are added
 * in later phases.
 *
 * See PRD-X §TR1, §TR11 and ADR-003 for rationale.
 */

// --- Aspect marker interfaces ------------------------------------------------

/** View aspect for `text-content` — Konva-text rendering. */
export interface TextContentViewAspect {
  readonly _kind: 'text-content-view';
}

/** Edit aspect for `rich-edit` — ContentEditable overlay. */
export interface RichEditAspect {
  readonly _kind: 'rich-edit';
}

/** Edit aspect for `plain-edit` — plain-input overlay. */
export interface PlainEditAspect {
  readonly _kind: 'plain-edit';
}

/** View aspect for `background-color` — Konva fill. */
export interface BgColorViewAspect {
  readonly _kind: 'bg-color-view';
}

/** Edit aspect for `background-color` — optionsbar color picker. */
export interface BgColorEditAspect {
  readonly _kind: 'bg-color-edit';
}

/** View aspect for `border-style` — Konva stroke. */
export interface BorderStyleViewAspect {
  readonly _kind: 'border-style-view';
}

/** Edit aspect for `border-style` — optionsbar stroke picker. */
export interface BorderStyleEditAspect {
  readonly _kind: 'border-style-edit';
}

/** View aspect for `font-size` — Konva font-size. */
export interface FontSizeViewAspect {
  readonly _kind: 'font-size-view';
}

/** Edit aspect for `font-size` — optionsbar slider. */
export interface FontSizeEditAspect {
  readonly _kind: 'font-size-edit';
}

/** View aspect for `font-style` — Konva font-style. */
export interface FontStyleViewAspect {
  readonly _kind: 'font-style-view';
}

/** Edit aspect for `font-style` — optionsbar bold/italic toggles. */
export interface FontStyleEditAspect {
  readonly _kind: 'font-style-edit';
}

/** Edit aspect for `resize` — Konva transformer. */
export interface ResizeEditAspect {
  readonly _kind: 'resize-edit';
}

/** Edit aspect for `rotation` — rotation handle. */
export interface RotationEditAspect {
  readonly _kind: 'rotation-edit';
}

/** View aspect for `anchor-target` — passive anchor-points exposure. */
export interface AnchorTargetViewAspect {
  readonly _kind: 'anchor-target-view';
}

/** View aspect for `stack-presence` — stack-panel visibility. */
export interface StackPresenceViewAspect {
  readonly _kind: 'stack-presence-view';
}

/** Edit aspect for `updatable-properties` — generic update path. */
export interface UpdatablePropertiesEditAspect {
  readonly _kind: 'updatable-properties-edit';
}

/** View aspect for `smiley-bitmap` — bitmap-cache render (P5.3). */
export interface SmileyBitmapViewAspect {
  readonly _kind: 'smiley-bitmap-view';
}

/** View aspect for `line-path-strategy` — Konva-line path computation (P5.5). */
export interface LinePathStrategyViewAspect {
  readonly _kind: 'line-path-strategy-view';
}

/** View aspect for `list-type` — bullet/ordered/checklist rendering (P5.6). */
export interface ListTypeViewAspect {
  readonly _kind: 'list-type-view';
}

/** Edit aspect for `list-type` — optionsbar list-type picker (P5.6). */
export interface ListTypeEditAspect {
  readonly _kind: 'list-type-edit';
}

// --- Aspect singletons -------------------------------------------------------

export const TEXT_CONTENT_VIEW: TextContentViewAspect = Object.freeze({
  _kind: 'text-content-view',
});

export const RICH_EDIT: RichEditAspect = Object.freeze({ _kind: 'rich-edit' });

export const PLAIN_EDIT: PlainEditAspect = Object.freeze({ _kind: 'plain-edit' });

export const BG_COLOR_VIEW: BgColorViewAspect = Object.freeze({
  _kind: 'bg-color-view',
});

export const BG_COLOR_EDIT: BgColorEditAspect = Object.freeze({
  _kind: 'bg-color-edit',
});

export const BORDER_STYLE_VIEW: BorderStyleViewAspect = Object.freeze({
  _kind: 'border-style-view',
});

export const BORDER_STYLE_EDIT: BorderStyleEditAspect = Object.freeze({
  _kind: 'border-style-edit',
});

export const FONT_SIZE_VIEW: FontSizeViewAspect = Object.freeze({
  _kind: 'font-size-view',
});

export const FONT_SIZE_EDIT: FontSizeEditAspect = Object.freeze({
  _kind: 'font-size-edit',
});

export const FONT_STYLE_VIEW: FontStyleViewAspect = Object.freeze({
  _kind: 'font-style-view',
});

export const FONT_STYLE_EDIT: FontStyleEditAspect = Object.freeze({
  _kind: 'font-style-edit',
});

export const RESIZE_EDIT: ResizeEditAspect = Object.freeze({ _kind: 'resize-edit' });

export const ROTATION_EDIT: RotationEditAspect = Object.freeze({
  _kind: 'rotation-edit',
});

export const ANCHOR_TARGET_VIEW: AnchorTargetViewAspect = Object.freeze({
  _kind: 'anchor-target-view',
});

export const STACK_PRESENCE_VIEW: StackPresenceViewAspect = Object.freeze({
  _kind: 'stack-presence-view',
});

export const UPDATABLE_PROPERTIES_EDIT: UpdatablePropertiesEditAspect = Object.freeze({
  _kind: 'updatable-properties-edit',
});

export const SMILEY_BITMAP_VIEW: SmileyBitmapViewAspect = Object.freeze({
  _kind: 'smiley-bitmap-view',
});

export const LINE_PATH_STRATEGY_VIEW: LinePathStrategyViewAspect = Object.freeze({
  _kind: 'line-path-strategy-view',
});

export const LIST_TYPE_VIEW: ListTypeViewAspect = Object.freeze({
  _kind: 'list-type-view',
});

export const LIST_TYPE_EDIT: ListTypeEditAspect = Object.freeze({
  _kind: 'list-type-edit',
});

// --- Discriminated union -----------------------------------------------------

/**
 * Discriminated union of all capability descriptors. Each arm carries
 * `capability` as discriminator plus optional/required `view` and/or `edit`
 * aspect markers.
 */
export type CapabilityDescriptor =
  | { readonly capability: 'text-content'; readonly view: TextContentViewAspect }
  | { readonly capability: 'rich-edit'; readonly edit: RichEditAspect }
  | { readonly capability: 'plain-edit'; readonly edit: PlainEditAspect }
  | {
      readonly capability: 'background-color';
      readonly view?: BgColorViewAspect;
      readonly edit?: BgColorEditAspect;
    }
  | {
      readonly capability: 'border-style';
      readonly view?: BorderStyleViewAspect;
      readonly edit?: BorderStyleEditAspect;
    }
  | {
      readonly capability: 'font-size';
      readonly view?: FontSizeViewAspect;
      readonly edit?: FontSizeEditAspect;
    }
  | {
      readonly capability: 'font-style';
      readonly view?: FontStyleViewAspect;
      readonly edit?: FontStyleEditAspect;
    }
  | { readonly capability: 'resize'; readonly edit: ResizeEditAspect }
  | { readonly capability: 'rotation'; readonly edit: RotationEditAspect }
  | { readonly capability: 'anchor-target'; readonly view: AnchorTargetViewAspect }
  | { readonly capability: 'stack-presence'; readonly view: StackPresenceViewAspect }
  | {
      readonly capability: 'updatable-properties';
      readonly edit: UpdatablePropertiesEditAspect;
    }
  | { readonly capability: 'smiley-bitmap'; readonly view: SmileyBitmapViewAspect }
  | {
      readonly capability: 'line-path-strategy';
      readonly view: LinePathStrategyViewAspect;
    }
  | {
      readonly capability: 'list-type';
      readonly view?: ListTypeViewAspect;
      readonly edit?: ListTypeEditAspect;
    };
